import React, { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import cx from 'classnames';

import styles from './MarkdownIconButton.module.scss';
import { CopyIcon, CheckIcon } from '../CodeBlockV2/icons';
import { useTrack } from 'src/contexts/AnalyticsContext';
import { Events, MARKDOWN_BUTTON_TRACKING_IDS } from 'src/lib/trackingEvents';
import { isMarkdownExcluded } from 'src/lib/markdown-config';

interface MarkdownIconButtonProps {
  sectionId?: string;
}

/**
 * MOCKUP: Icon-only markdown button for placement after h4 headings
 * Compact version with just the icon, no text label
 */
const MarkdownIconButton: React.FC<MarkdownIconButtonProps> = ({
  sectionId,
}) => {
  const { asPath, basePath } = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const track = useTrack();

  // Convert /docs/link/overview to /docs/link/overview/index.html.md
  // Also handles /core-exchange/docs/ paths
  const getMarkdownUrl = () => {
    let path = asPath;
    // Remove hash fragment if present (e.g., #endpoint-name)
    const hashIndex = path.indexOf('#');
    if (hashIndex !== -1) {
      path = path.substring(0, hashIndex);
    }
    // Remove query params if present
    const queryIndex = path.indexOf('?');
    if (queryIndex !== -1) {
      path = path.substring(0, queryIndex);
    }
    // Remove trailing slash
    if (path.endsWith('/')) {
      path = path.substring(0, path.length - 1);
    }
    // Add index.html.md using current origin and basePath for environment-aware URLs
    // basePath includes commit hash on staging (e.g., /1c36fa) and is empty on production
    const origin =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'https://plaid.com';
    return `${origin}${basePath}${path}/index.html.md`;
  };

  const markdownUrl = getMarkdownUrl();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const extractSection = (markdown: string, headingId: string): string => {
    const lines = markdown.split('\n');
    let sectionStart = -1;
    let sectionEnd = lines.length;
    let currentHeadingLevel = 0;

    // Find the heading with the matching ID
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Match markdown headings (#### for h4)
      const headingMatch = line.match(/^(#{1,6})\s+(.+)/);
      if (headingMatch) {
        const level = headingMatch[1].length;
        const headingText = headingMatch[2];

        // Convert heading text to ID format (lowercase, replace spaces with hyphens)
        const generatedId = headingText
          .toLowerCase()
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens

        if (
          generatedId === headingId ||
          headingText.includes(`{#${headingId}}`)
        ) {
          sectionStart = i;
          currentHeadingLevel = level;
          break;
        }
      }
    }

    if (sectionStart === -1) {
      return markdown;
    }

    // Find the end of this section - stop at \=\*=\*=\*= separators
    for (let i = sectionStart + 1; i < lines.length; i++) {
      const line = lines[i];
      const isSeparator = /^\\=(\\\*=)+$/.test(line.trim());
      if (isSeparator) {
        sectionEnd = i;
        break;
      }
    }

    return lines.slice(sectionStart, sectionEnd).join('\n');
  };

  const handleCopyMarkdown = async () => {
    try {
      // Fetch markdown directly from the public URL
      const response = await fetch(markdownUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch markdown: ${response.status}`);
      }

      let markdownContent = await response.text();

      // If we have a sectionId, extract just that section
      if (sectionId) {
        markdownContent = extractSection(markdownContent, sectionId);
      }

      await navigator.clipboard.writeText(markdownContent);
      setCopySuccess(true);

      track({
        type: Events.MARKDOWN_BUTTON_CLICKED,
        payload: {
          markdownAction: MARKDOWN_BUTTON_TRACKING_IDS.ICON_BUTTON_COPIED,
          sectionId,
        },
      });

      setTimeout(() => {
        setCopySuccess(false);
        setIsOpen(false);
      }, 2000);
    } catch (err) {
      track({
        type: Events.ERRORED,
        payload: {
          markdownAction: MARKDOWN_BUTTON_TRACKING_IDS.ICON_BUTTON_COPY_FAILED,
          sectionId,
          error: err instanceof Error ? err.message : 'Unknown error',
        },
      });

      window.open(markdownUrl, '_blank');
      alert(
        'Could not copy automatically. The markdown file has been opened in a new tab. Please copy it manually.',
      );
      setIsOpen(false);
    }
  };

  if (isMarkdownExcluded(asPath)) {
    return null;
  }

  return (
    <div className={styles.markdownIconContainer} ref={dropdownRef}>
      <button
        type='button'
        className={cx(styles.markdownIconButton, {
          [styles.markdownIconButtonOpen]: isOpen,
        })}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          const newIsOpen = !isOpen;
          setIsOpen(newIsOpen);

          if (newIsOpen) {
            track({
              type: Events.MARKDOWN_BUTTON_CLICKED,
              payload: {
                markdownAction: MARKDOWN_BUTTON_TRACKING_IDS.ICON_BUTTON_OPENED,
                sectionId,
              },
            });
          }
        }}
        onMouseDown={(e) => {
          // Prevent focus to avoid triggering Schema scroll logic
          e.preventDefault();
        }}
        aria-label='Markdown options'
        aria-expanded={isOpen}
      >
        <svg
          className={styles.icon}
          width='16'
          height='16'
          viewBox='0 0 16 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
          aria-hidden='true'
        >
          <path
            d='M14 4.5V14C14 14.2652 13.8946 14.5196 13.7071 14.7071C13.5196 14.8946 13.2652 15 13 15H3C2.73478 15 2.48043 14.8946 2.29289 14.7071C2.10536 14.5196 2 14.2652 2 14V2C2 1.73478 2.10536 1.48043 2.29289 1.29289C2.48043 1.10536 2.73478 1 3 1H11.5L14 4.5Z'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <path
            d='M11 1V5H14'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
          <path
            d='M5 8H11M5 11H11'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          <a
            href={markdownUrl}
            className={styles.dropdownItem}
            target='_blank'
            rel='noopener noreferrer'
            onClick={(e) => {
              e.stopPropagation();
              track({
                type: Events.MARKDOWN_BUTTON_CLICKED,
                payload: {
                  markdownAction:
                    MARKDOWN_BUTTON_TRACKING_IDS.ICON_BUTTON_VIEWED_IN_NEW_PAGE,
                  sectionId,
                },
              });
            }}
          >
            <svg
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M13 9V13H3V9M8 11V3M8 3L11 6M8 3L5 6'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            View page as markdown
          </a>
          <button
            className={styles.dropdownItem}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCopyMarkdown();
            }}
            onMouseDown={(e) => {
              // Prevent focus to avoid triggering Schema scroll logic
              e.preventDefault();
            }}
            type='button'
          >
            {copySuccess ? <CheckIcon /> : <CopyIcon />}
            {copySuccess
              ? 'Copied!'
              : sectionId
              ? 'Copy section as markdown'
              : 'Copy page as markdown'}
          </button>
        </div>
      )}
    </div>
  );
};

MarkdownIconButton.displayName = 'MarkdownIconButton';
export default MarkdownIconButton;
